import bilAPI from "./api";

// Build the create/update CONTENT body. Recommendation rules are NOT part of
// the material write — they live on per-school overlays edited via the
// adaptation endpoint below. When a File upload is present the request must be
// multipart/form-data, and because multipart can't carry nested JSON the
// `subjects` array is sent as a JSON string (the backend decodes it).
const buildMaterialRequest = (body = {}) => {
  const hasFile = body.file instanceof File;
  if (!hasFile) {
    const payload = {
      title: body.title,
      description: body.description ?? "",
      resource_type: body.resource_type,
      subjects: body.subjects || [],
    };
    // Only send `url` when the caller provides it. On a file-mode edit that
    // keeps the existing upload, `url` is absent — omitting it leaves the
    // stored value untouched (a "" would blank it).
    if (body.url !== undefined) payload.url = body.url;
    return { payload, config: {} };
  }

  const fd = new FormData();
  fd.append("title", body.title ?? "");
  fd.append("description", body.description ?? "");
  fd.append("resource_type", body.resource_type ?? "");
  if (body.url) fd.append("url", body.url);
  fd.append("file", body.file);
  fd.append("subjects", JSON.stringify(body.subjects || []));
  return {
    payload: fd,
    config: { headers: { "Content-Type": "multipart/form-data" } },
  };
};

// -- per-school recommendation overlay ---------------------------------------

// GET the caller-school's adaptation for a material:
//   { organization, inheriting_default, active_version, rules, default_rules }
export const getMaterialAdaptation = ({ uuid, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .get(`/materials/materials/${uuid}/adaptation/`, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

// PUT the caller-school's rules (replaces only this school's overlay).
// `rules` = [{ question, operator, expected_option }].
export const putMaterialAdaptation = ({ uuid, rules = [], signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .put(`/materials/materials/${uuid}/adaptation/`, { rules }, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

// DELETE the caller-school's overlay (revert to the owner default).
export const deleteMaterialAdaptation = ({ uuid, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .delete(`/materials/materials/${uuid}/adaptation/`, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

export const getMaterials = ({
  params: { search, ordering, page } = {},
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .get(`/materials/materials/`, {
          params: { search, ordering, page },
          signal,
        })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

export const createMaterial = ({ body, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      const { payload, config } = buildMaterialRequest(body);
      bilAPI
        .post(`/materials/materials/`, payload, { ...config, signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

export const updateMaterial = ({ uuid, body, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      const { payload, config } = buildMaterialRequest(body);
      bilAPI
        .patch(`/materials/materials/${uuid}/`, payload, { ...config, signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

export const deleteMaterial = ({ uuid, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .delete(`/materials/materials/${uuid}/`, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

// -- teacher-facing -----------------------------------------------------------

// Live recommendation engine. Resolves with a PLAIN ARRAY (not paginated) of
// { material: <MaterialRead incl my_state>, matched_questions: [<text>, ...] }.
export const getRecommendedMaterials = ({ signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .get(`/materials/materials/recommended/`, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

// Materials visible to the caller, each annotated with my_state. Paginated
// like the admin list: { count, next, previous, results }.
export const getAvailableMaterials = ({
  params: { search, page, saved } = {},
  signal,
} = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .get(`/materials/materials/available/`, {
          params: { search, page, saved: saved || undefined },
          signal,
        })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

export const saveMaterial = ({ uuid, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .post(`/materials/materials/${uuid}/save/`, null, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

export const unsaveMaterial = ({ uuid, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .post(`/materials/materials/${uuid}/unsave/`, null, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

export const markMaterialRead = ({ uuid, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .post(`/materials/materials/${uuid}/read/`, null, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};

// Persist the caller's resume point for a material. `body` carries `seconds`
// (video playback position) and/or `page` (PDF page) — send only the field(s)
// you're updating; the backend ignores absent/invalid values so a partial
// update never clobbers the other. Resolves with the material (incl my_state,
// which now also carries progress_seconds / progress_page).
export const updateMaterialProgress = ({ uuid, body = {}, signal } = {}) => {
  return new Promise((resolve, reject) => {
    try {
      bilAPI
        .post(`/materials/materials/${uuid}/progress/`, body, { signal })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    } catch (error) {
      reject(error);
    }
  });
};
